/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */

// 3rd party libraries
import React from 'react';
import { useSelector } from 'react-redux';
import NewFlow from '@popups/NewFlow';
import NewRun from '@popups/NewRun';
import { hidePopup, popupSelector } from '@/stores/slices/appPopup';
import AppConstants from '@utils/AppConstants';
import { useCustomCompareMemo } from 'use-custom-compare';
import { isEqual } from 'lodash/fp';
import IntegrateBookPopup from '@/components/IntegrateBookPopup/IntegrateBookPopup';
import CreateDepartmentPopup from './CreateDepartmentPopup/CreateDepartmentPopup';
import CreateOrganizationPopup from './CreateOrganizationPopup';
import DepartmentCollaboratorsPopup from './DepartmentCollaboratorsPopup';
import RawFactViewerPopup from './RawFactViewerPopup';
import ObjectViewerPopup from './ObjectViewerPopup';
import S3FileViewerPopup from './S3FileViewerPopup';
import { useAppDispatch } from '../stores/hooks';
import MarkdownViewerPopup from './MarkdownViewerPopup';
import MarkdownEditorPopup from './MarkdownEditorPopup';
import ViewRequestPopup from './ViewRequestPopup';
import HTMLViewerPopup from './HTMLViewerPopup';
import DepartmentCollaboratorsAddPopup from './DepartmentCollaboratorsAddPopup';
import AnswerCSVPopup from './AnswerCSVPopup';
import AnswerConceptsTablePopup from './AnswerConceptsTablePopup';
import RequestExceptionsBatchAnswerPopup from './RequestExceptionsBatchAnswerPopup';
import DocumentExceptionEditorPopup from './DocumentExceptionEditorPopup';
import ProcedureExceptionRoutingPopup from './ProcedureExceptionRoutingPopup';
import FullMapPopup from './FullMapPopup/FullMapPopup';
import MiniPlaygroundPopup from './MiniPlaygroundPopup';

function CurrentPopup() {
  const dispatch = useAppDispatch();
  const { currentActivePopups } = useSelector(popupSelector);

  const popupComponentMap = {
    [AppConstants.POPUPS.ANSWER_CSV]: AnswerCSVPopup,
    [AppConstants.POPUPS.ANSWER_CONCEPTS_TABLE]: AnswerConceptsTablePopup,
    [AppConstants.POPUPS.NEW_FLOW]: NewFlow,
    [AppConstants.POPUPS.NEW_RUN]: NewRun,
    // [AppConstants.POPUPS.EXPORT_GRID]: Export,
    [AppConstants.POPUPS.CREATE_ORGANIZATION]: CreateOrganizationPopup,
    [AppConstants.POPUPS.CREATE_DEPARTMENT]: CreateDepartmentPopup,
    [AppConstants.POPUPS.DEPARTMENT_COLLABORATORS]:
      DepartmentCollaboratorsPopup,
    [AppConstants.POPUPS.DEPARTMENT_COLLABORATORS_ADD]:
      DepartmentCollaboratorsAddPopup,
    [AppConstants.POPUPS.REQUEST_EXCEPTIONS_BATCH_ANSWER]:
      RequestExceptionsBatchAnswerPopup,
    [AppConstants.POPUPS.VIEW_REQUEST]: ViewRequestPopup,
    [AppConstants.POPUPS.VIEW_OBJECT]: ObjectViewerPopup,
    [AppConstants.POPUPS.VIEW_RAW_FACT]: RawFactViewerPopup,
    [AppConstants.POPUPS.VIEW_S3_FILE]: S3FileViewerPopup,
    [AppConstants.POPUPS.MARKDOWN_VIEWER]: MarkdownViewerPopup,
    [AppConstants.POPUPS.MARKDOWN_EDITOR]: MarkdownEditorPopup,
    [AppConstants.POPUPS.HTML_VIEWER]: HTMLViewerPopup,
    [AppConstants.POPUPS.DOCUMENT_EXCEPTION_EDITOR]:
      DocumentExceptionEditorPopup,
    [AppConstants.POPUPS.CREDENTIAL_INTEGRATION]: IntegrateBookPopup,
    [AppConstants.POPUPS.PROCEDURE_EXCEPTION_ROUTING]:
      ProcedureExceptionRoutingPopup,
    [AppConstants.POPUPS.FULL_MAP]: FullMapPopup,
    [AppConstants.POPUPS.MINI_PLAYGROUND]: MiniPlaygroundPopup
  };

  const popupMemo = useCustomCompareMemo(
    () => (
      <div>
        {Object.keys(currentActivePopups).map((popupId) => {
          const Komponent = popupComponentMap[popupId] as any;
          const params = currentActivePopups[popupId];
          return (
            <Komponent
              key={popupId}
              {...params}
              onClose={() => {
                dispatch(
                  hidePopup({
                    popupId
                  })
                );
              }}
            />
          );
        })}
      </div>
    ),
    [Object.keys(currentActivePopups)],
    (prevDeps, nextDeps) => isEqual(prevDeps, nextDeps)
  );

  return popupMemo;
}

export default CurrentPopup;
